import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthentication } from 'src/features/authentication/context';
import { AlarmsService } from 'src/services/alarms';
import { AlarmDataV3 } from 'src/types/alarms';

type ArgsType = {
  siteId: string;
  filters: any;
  rowsPerPage: any;
};

export const useGetAllSiteAlarms = ({ siteId, filters, rowsPerPage }: ArgsType) => {
  const {
    customerId: { value: customerId },
  } = useAuthentication();

  const [alarms, setAlarmData] = useState<AlarmDataV3[] | []>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);

  const getAllSiteAlarms = async ({
    siteId,
    limit,
    skipTo,
    skipToSiteId,
    filters,
  }: {
    siteId: string;
    limit: number;
    skipTo?: string;
    skipToSiteId?: string;
    filters: any;
  }) => {
    if (!customerId) return;

    filters = filters + `&siteIdList=${siteId}`;

    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel('Request canceled.');
    }

    const cancelTokenSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelTokenSource;

    setIsLoaded(false);

    try {
      const response = await AlarmsService.getAll(
        customerId,
        limit,
        skipTo,
        skipToSiteId,
        undefined,
        cancelTokenSource.token,
        filters
      );
      setAlarmData(response);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoaded(true);
    }
  };

  const onAcknowledged = useCallback(
    (response: AlarmDataV3) => {
      setAlarmData((prevState) =>
        prevState.map((item) =>
          item.id === response.id ? { ...item, acknowledgement: response.acknowledgement } : item
        )
      );
    },
    [setAlarmData]
  );

  useEffect(() => {
    getAllSiteAlarms({ siteId: siteId, limit: rowsPerPage, skipTo: '', filters: filters });

    return () => {
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Filters changed.');
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, siteId, filters]);

  return { alarms, isLoaded, getAllSiteAlarms, setAlarmData, onAcknowledged };
};
